import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "electrician-web-design-colorado",
  canonicalOverride: "https://coloradowebimpressions.com/electrical-web-design-colorado",
  metaTitle: "Electrical Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom electrical web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Electrical contractor marketing", to: "/electrical-contractor-marketing-colorado" },
  ],
  currentLabel: "Electrical Contractor Web Design Colorado",
  eyebrow: "Electrical Contractor Web Design — Colorado",
  h1: (
    <>
      Electrical Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded electrical websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Electrical websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first electrical sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Electrical Contractor web design in Colorado",
    body: "Electrical Contractor web design is the practice of building conversion-focused, mobile-first websites for electrical companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second load on mobile, 95+ PageSpeed — no bloated themes." },
    { title: "Service pages per trade", body: "Panel upgrades, EV chargers, generators, rewires, lighting — each ranks independently." },
    { title: "Sticky click-to-call", body: "Phone number stays visible across every scroll, every page, every device." },
    { title: "Permit & licensing trust signals", body: "License number, bond, and insurance badges baked into header and footer." },
    { title: "Estimate request forms", body: "Conversion-tested forms tuned for residential and commercial estimate flows." },
    { title: "Schema and SEO from day one", body: "LocalBusiness, Service, FAQPage, and Review schema shipped at launch." },
  ],
  whyThisMarket: [
    { heading: "EV charger installs are the new lead magnet", body: "Search volume for residential EV charger installation in Colorado has tripled since 2022. Dedicated service pages capture that demand before generalists do." },
    { heading: "Most electrical sites bury services in dropdowns", body: "We build full ranking pages for every service line — panel upgrades, generators, lighting design — so each earns Map Pack association." },
    { heading: "Trust signals decide who gets the call", body: "Homeowners hiring an electrician care about license, bond, and reviews before price. We surface that proof above the fold." },
  ],
  relatedLinks: [
    { to: "/electrical-contractor-marketing-colorado", label: "Electrical contractor marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/electrical-seo-colorado", label: "Electrical Contractor SEO in Colorado" },
    { to: "/electrical-google-maps-colorado", label: "Electrical Contractor Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado electrical case studies" },
    { to: "/wix-vs-custom-website-colorado-electrician", label: "Wix vs custom website for Electrician" },
    { to: "/squarespace-vs-custom-website-colorado-electrician", label: "Squarespace vs custom website for Electrician" },
    { to: "/godaddy-vs-custom-website-colorado-electrician", label: "GoDaddy vs custom website for Electrician" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a electrical website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a electrical website?", a: "Typical timeline is 4–6 weeks for a 12–20 page electrical site with full service and service-area pages. Larger multi-location electrical companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs electrical companies?", a: "No — we build electrical sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Electrical Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for electrical companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/electrician-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
